// 균열 발광 바이옴 — 바닥을 «재질색»으로 다시 칠하고, 절차 균열(보로노이 경계)로 틈을 어둡게 곱한 뒤(캐비티)
// 틈 안을 빛나게 한다(이미시브 → 블룸이 후광). 텍스처 장·굽기 지문(BIOME_TOP_SIG)과 무관한 런타임 무늬다.
// 가중치·id 는 biomeMaterialField 의 C 텍스처(r=가중치, g=id×32)에서 읽는다. 절차: skill loc-effect-3d/reference/light/crack.md.
import * as THREE from 'three';
import { CLOUD_TIME } from '../sky/skyLight.js';
import { BIOMES } from './biomeDefs.js';

const lin = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
};

/**
 * 키 → 무늬 값. id 는 1부터, 7까지(C 텍스처 g 채널 = id×32).
 *   base     바닥 재질색(sRGB hex) · baseAmt 덮는 세기(0~1, 텍스처 결의 밝기는 남긴다)
 *   kind     'crack' = 망 모양 균열 · 'shard' = 칸마다 박힌 결정·발광 점
 *   cell     무늬 한 칸(m) · width 균열 폭(칸 비율) · density 켜지는 칸 비율 · cavity 틈 어둡게(0~1)
 *   glow     빛 색(sRGB hex) · glowAmt 이미시브 세기 · night 밤 배율 가산
 *   pool     통째로 빛나는 칸 비율(용암 웅덩이) · poolR 웅덩이 반지름(칸 비율) · warp 균열 굽이침(칸 비율)
 *   flow     빛이 흐르는 속도(0 = 멈춤) · spill 틈 밖으로 새는 후광 세기 · pulse 맥동 깊이 · fine 잔금 마스크 세기
 *   depth    틈 깊이(m) — 시차로 빛을 표면 아래에서 읽고, 높이장(범프 노멀·골 그늘)을 그만큼 깎는다
 *   masks    잔금 아틀라스 두 칸 [a, b] — 0 가지 균열 · 1 굵은 용암 강 · 2 깨진 판 · 3 실금(빙렬). 넓은 얼룩 단위로 섞는다
 *   rim      경계 안쪽 테 그늘 · sideAmt 옆면·절벽 덮는 세기(색은 biomeDefs 의 cliff·foliage·understory)
 *   scorch   경계 바깥 띠 그을림 색 · scorchK 그 밝기 배율(어두운 바이옴 0.55~0.62, 천상 1.0)
 * 크기 범위(칸 2~3.5m · 폭 0.045~0.065 · 웅덩이 2% 이하)는 skill loc-effect-3d/reference/light/crack.md §0-e.
 */
export const CRACK_BIOMES = {
  volcanic: { id: 1, base: '#3a302c', baseAmt: 1, kind: 'crack', cell: 2.6, width: 0.065, density: 0.5, cavity: 0.85, glow: '#ff6a12', glowAmt: 3.0, night: 1.4, pool: 0.02, poolR: 0.28, warp: 0.4, flow: 0.35, spill: 0.45, pulse: 0.45, fine: 0.55, masks: [0, 1], depth: 0.3, rim: 0.35, sideAmt: 0.92, scorch: '#5e5249', scorchK: 0.62 },
  infernal: { id: 2, base: '#6a1a1e', baseAmt: 1, kind: 'crack', cell: 2.3, width: 0.06, density: 0.5, cavity: 0.7, glow: '#ff2a55', glowAmt: 2.6, night: 1.6, pool: 0.01, poolR: 0.25, warp: 0.35, flow: 0.5, spill: 0.5, pulse: 0.55, fine: 0.6, masks: [2, 0], depth: 0.26, rim: 0.3, sideAmt: 0.92, scorch: '#4a1e1e', scorchK: 0.55 },
  crystal: { id: 3, base: '#9a8fc4', baseAmt: 1, kind: 'crack', cell: 2.5, width: 0.05, density: 0.55, cavity: 0.45, glow: '#b583ff', glowAmt: 2.2, night: 1.2, pool: 0, poolR: 0, warp: 0.35, flow: 0.25, spill: 0.6, pulse: 0.35, fine: 0.35, masks: [2, 3], depth: 0.15, rim: 0.15, sideAmt: 0.88, scorch: '#7c7290', scorchK: 0.85 },
  abyss: { id: 4, base: '#22223f', baseAmt: 1, kind: 'shard', cell: 1.4, width: 0.1, density: 0.42, cavity: 0.3, glow: '#33f0ff', glowAmt: 2.0, night: 1.0, pool: 0, poolR: 0, warp: 0.2, flow: 0.4, spill: 0.4, pulse: 0.6, fine: 0.25, masks: [3, 0], depth: 0.16, rim: 0.3, sideAmt: 0.92, scorch: '#3c4258', scorchK: 0.6 },
  celestial: { id: 5, base: '#d8d2c4', baseAmt: 1, kind: 'crack', cell: 3.2, width: 0.035, density: 0.38, cavity: 0.06, glow: '#ffd27a', glowAmt: 1.1, night: 0.7, pool: 0, poolR: 0, warp: 0.12, flow: 0.12, spill: 0.2, pulse: 0.2, fine: 0.22, masks: [3, 3], depth: 0.04, rim: 0.1, sideAmt: 0.85, scorch: '#b8a87c', scorchK: 1.0 },
};
export const CRACK_NAMES = Object.keys(CRACK_BIOMES);
/** 동굴 면(바닥·벽·천장) 전용 슬롯 — 바이옴 필드에는 안 깔리고 동굴 부피 게이트(CAVE_CRACK_GLSL)만 부른다.
 *  cell = 주 균열 칸(m, 중간 금은 0.43배·실금은 아틀라스) · width = 선 폭(칸 비율) · density = 칸마다 켜지는 확률 · fine = 실금 세기. */
export const CAVE_CRACK = {
  amethyst: { id: 6, base: '#463a68', baseAmt: 0.2, kind: 'crack', cell: 0.8, width: 0.06, density: 0.45, cavity: 0.55, glow: '#b06bff', glowAmt: 2.0, night: 0, pool: 0, poolR: 0, warp: 0, flow: 0.3, spill: 0.5, pulse: 0.45, fine: 0.4, masks: [0, 3], depth: 0.08 },
  teal: { id: 7, base: '#26485a', baseAmt: 0.2, kind: 'crack', cell: 0.75, width: 0.065, density: 0.72, cavity: 0.5, glow: '#33f0d2', glowAmt: 2.6, night: 0, pool: 0, poolR: 0, warp: 0, flow: 0.4, spill: 0.45, pulse: 0.5, fine: 0.75, masks: [2, 3], depth: 0.07 },
  magma: { id: 8, base: '#2a1410', baseAmt: 0.2, kind: 'crack', cell: 1.1, width: 0.08, density: 0.6, cavity: 0.7, glow: '#ff5a1c', glowAmt: 3.0, night: 0, pool: 0, poolR: 0, warp: 0, flow: 0.55, spill: 0.6, pulse: 0.35, fine: 0.5, masks: [0, 1], depth: 0.1 },
  abyss: { id: 9, base: '#101828', baseAmt: 0.2, kind: 'crack', cell: 0.8, width: 0.05, density: 0.5, cavity: 0.5, glow: '#3a7bff', glowAmt: 2.2, night: 0, pool: 0, poolR: 0, warp: 0, flow: 0.25, spill: 0.45, pulse: 0.55, fine: 0.6, masks: [2, 3], depth: 0.08 },
  starlight: { id: 10, base: '#07080f', baseAmt: 0.35, kind: 'crack', cell: 0.9, width: 0.04, density: 0.5, cavity: 0.6, glow: '#bfe0ff', glowAmt: 2.4, night: 0, pool: 0, poolR: 0, warp: 0, flow: 0.2, spill: 0.4, pulse: 0.6, fine: 0.7, masks: [2, 3], depth: 0.08 },
  stargold: { id: 11, base: '#0b0906', baseAmt: 0.3, kind: 'crack', cell: 1.2, width: 0.05, density: 0.4, cavity: 0.55, glow: '#ffcf6a', glowAmt: 2.6, night: 0, pool: 0, poolR: 0, warp: 0, flow: 0.3, spill: 0.5, pulse: 0.45, fine: 0.5, masks: [0, 3], depth: 0.09 },
};
/** 동굴 테마 — 지금 부피 게이트에 실린 동굴이 고른다(caveCrystalZone). 정동 = 종전 값 그대로. */
export const CAVE_THEMES = { geode: [0, 6, 7], magma: [1, 8, 9], starlight: [2, 10, 11] };   // x: 0 정동 · 1 마그마 · 2 흑요 별빛
export const CRACK_SLOTS = 12;
export const crackIdOf = (key) => CRACK_BIOMES[key]?.id || 0;

const slots = () => Array.from({ length: CRACK_SLOTS }, () => new THREE.Vector4());
export const CRACK_BASE = { value: slots() };    // rgb 선형 + baseAmt
export const CRACK_GLOW = { value: slots() };    // rgb 선형 + glowAmt
export const CRACK_SHAPE = { value: slots() };   // cell · width · density · cavity
export const CRACK_MISC = { value: slots() };    // kind(0 균열 · 1 결정) · night · pool · warp
export const CRACK_ANIM = { value: slots() };   // flow · spill · pulse · fine
export const CRACK_MASK = { value: null };       // 잔금 알파 아틀라스(2×2, 0~1 흑백) — 없으면 잔금만 빠진다
export const CRACK_MASKSEL = { value: slots() }; // 칸 a · 칸 b · 섞는 얼룩 크기(m) · 틈 깊이(m)
export const CRACK_SIDE = { value: slots() };    // 옆면·절벽·줄기 색(선형) + sideAmt
export const CRACK_LEAF = { value: slots() };    // 수관 색(선형) + 세기
export const CRACK_UNDER = { value: slots() };   // 지면 식생 색(선형) + 세기
export const CRACK_EDGEP = { value: slots() };   // poolR · rim · 경계 굽이 배율 · (비움)
export const CRACK_SCORCH = { value: slots() };  // 경계 그을림 색(선형) + 밝기 배율
export const CRACK_ON = { value: 1 };            // 0 이면 분기 통째로 꺼짐(종전과 픽셀 동일)
const urlNum = (key, def) => {
  try {
    const v = new URLSearchParams(window.location.search).get(key);
    return v === null ? def : Math.max(0, Math.min(1, parseFloat(v) || 0));
  } catch { return def; }
};
/** 경계 섞기 — 0(주소줄 ?ckedge=0)이면 선형 가중치를 그대로 쓴다. */
export const CRACK_EDGE_ON = { value: urlNum('ckedge', 1) };
/** 필드 C 텍스처 홀더 — biomeMaterialField 가 같은 객체를 BIOME_MATERIAL_C·XF·ON 으로 내보낸다. */
export const CRACK_FIELD_TEX = { value: null };
export const CRACK_FIELD_XF = { value: new THREE.Vector4(0, 0, 1, 1) };
export const CRACK_FIELD_ON = { value: 0 };
for (const [key, b] of Object.entries(CRACK_BIOMES)) {
  const d = BIOMES[key] || {};
  CRACK_BASE.value[b.id].set(...lin(b.base), b.baseAmt);
  CRACK_GLOW.value[b.id].set(...lin(b.glow), b.glowAmt);
  CRACK_SHAPE.value[b.id].set(b.cell, b.width, b.density, b.cavity);
  CRACK_MISC.value[b.id].set(b.kind === 'shard' ? 1 : 0, b.night, b.pool || 0, b.warp || 0);
  CRACK_ANIM.value[b.id].set(b.flow || 0, b.spill || 0, b.pulse || 0, b.fine || 0);
  CRACK_MASKSEL.value[b.id].set(b.masks?.[0] ?? 0, b.masks?.[1] ?? 0, 38, b.depth || 0);
  CRACK_SIDE.value[b.id].set(...lin(d.cliff || b.base), b.sideAmt ?? 0.9);
  CRACK_LEAF.value[b.id].set(...lin(d.foliage || b.base), 0.92);
  CRACK_UNDER.value[b.id].set(...lin(d.understory || b.base), 0.85);
  CRACK_EDGEP.value[b.id].set(b.poolR || 0, b.rim || 0, 1, 0);
  CRACK_SCORCH.value[b.id].set(...lin(b.scorch || b.base), b.scorchK ?? 0.7);
}
for (const b of Object.values(CAVE_CRACK)) {
  CRACK_BASE.value[b.id].set(...lin(b.base), b.baseAmt);
  CRACK_GLOW.value[b.id].set(...lin(b.glow), b.glowAmt);
  CRACK_SHAPE.value[b.id].set(b.cell, b.width, b.density, b.cavity);
  CRACK_MISC.value[b.id].set(b.kind === 'shard' ? 1 : 0, b.night, b.pool, b.warp);
  CRACK_ANIM.value[b.id].set(b.flow, b.spill, b.pulse, b.fine);
  CRACK_MASKSEL.value[b.id].set(b.masks[0], b.masks[1], 38, b.depth);
  CRACK_EDGEP.value[b.id].set(b.poolR, 0, 1, 0);
  CRACK_SIDE.value[b.id].set(...lin(b.base), 0);
  CRACK_SCORCH.value[b.id].set(...lin(b.base), 1);
}

// ── 동굴 부피 게이트 — 선분(캡슐) 몇 개로 «이 면이 동굴 안인가»를 판다 ─────────────
// 자료는 place_cave_crystals.py 가 쓴 /data/cave_crystal_cave.json(CaveCrystalFx 가 싣는다).
// uCkCaveOn 은 카메라·플레이어가 그 상자 안 땅속일 때만 1 — 지상에서는 분기 비용이 0 이다.
export const CAVE_SEGS = 16;
export const CAVE_CRACK_ON = { value: 0 };
export const CAVE_CRACK_BOX = { value: new THREE.Vector4(0, 0, 0, 0) };   // xmin · zmin · xmax · zmax
export const CAVE_CRACK_SEG = { value: Array.from({ length: CAVE_SEGS }, () => new THREE.Vector4()) };   // x0 · z0 · x1 · z1
export const CAVE_CRACK_Y = { value: Array.from({ length: CAVE_SEGS }, () => new THREE.Vector4()) };     // 바닥 y0 · y1 · 반폭(0 = 끝) · 높이
export const CAVE_CRACK_THEME = { value: new THREE.Vector4(...CAVE_THEMES.geode, 0) };                  // 팔레트(0 정동 · 1 마그마) · 주 슬롯 · 강조 슬롯 · 갱구 페이드(m, 0 = 끔)
export const CAVE_CRACK_MOUTH = { value: new THREE.Vector4(1e9, 1e9, 1e9, 1e9) };
export const CAVE_CRACK_MOUTHY = { value: new THREE.Vector4(0, 0, 0, 0) };                             // 갱구 두 곳 바닥 y · 밖 번짐(halo) 세기                       // 갱구 두 곳 xz — 페이드 기준점
/** 주소줄 ?cavecrack=0 이면 동굴 균열을 통째로 끈다(A/B). */
export const CAVE_CRACK_ALLOWED = urlNum('cavecrack', 1) > 0.5;
if (typeof window !== 'undefined') window.__caveCrack = { CAVE_CRACK_ON, CAVE_CRACK_BOX };
/** zone = {box:[4], segs:[[x0,z0,x1,z1,y0,y1,hw,h]…], mouths?:[[x,z]×2], fade?:m} 를 유니폼에 싣는다. theme = CAVE_THEMES 키.
 *  fade > 0 이면 테마가 갱구에서 fade m 에 걸쳐 차오른다(입구에서 툭 바뀌지 않게). 없으면 종전과 픽셀 동일. */
export const setCaveCrackZone = (zone, theme = 'geode') => {
  const segs = zone?.segs || [];
  const b = zone?.box || [0, 0, 0, 0];
  CAVE_CRACK_BOX.value.set(b[0], b[1], b[2], b[3]);
  CAVE_CRACK_THEME.value.set(...(CAVE_THEMES[theme] || CAVE_THEMES.geode), zone?.fade || 0);
  const m = zone?.mouths || [];
  CAVE_CRACK_MOUTH.value.set(m[0]?.[0] ?? 1e9, m[0]?.[1] ?? 1e9, m[1]?.[0] ?? m[0]?.[0] ?? 1e9, m[1]?.[1] ?? m[0]?.[1] ?? 1e9);
  CAVE_CRACK_MOUTHY.value.set(m[0]?.[2] ?? 0, m[1]?.[2] ?? m[0]?.[2] ?? 0, zone?.halo ?? 0, 0);
  for (let i = 0; i < CAVE_SEGS; i += 1) {
    const s = segs[i];
    CAVE_CRACK_SEG.value[i].set(s ? s[0] : 0, s ? s[1] : 0, s ? s[2] : 0, s ? s[3] : 0);
    CAVE_CRACK_Y.value[i].set(s ? s[4] : 0, s ? s[5] : 0, s ? s[6] : 0, s ? s[7] : 0);
  }
};
export const caveCrackUniforms = {
  uCkCaveOn: CAVE_CRACK_ON, uCkCaveBox: CAVE_CRACK_BOX, uCkCaveSeg: CAVE_CRACK_SEG, uCkCaveY: CAVE_CRACK_Y,
  uCkCaveTheme: CAVE_CRACK_THEME, uCkCaveMouth: CAVE_CRACK_MOUTH, uCkCaveMouthY: CAVE_CRACK_MOUTHY,
};
/** 필드 조회·경계 섞기만 쓰는 재질(블록 배열 — 나무·풀·절벽)용. */
export const crackFieldUniforms = {
  uCkC: CRACK_FIELD_TEX, uCkXf: CRACK_FIELD_XF, uCkFOn: CRACK_FIELD_ON, uCkEdgeOn: CRACK_EDGE_ON,
  uCkBase: CRACK_BASE, uCkSide: CRACK_SIDE, uCkLeaf: CRACK_LEAF, uCkUnder: CRACK_UNDER, uCkEdgeP: CRACK_EDGEP, uCkScorch: CRACK_SCORCH,
};
export const crackUniforms = {
  ...crackFieldUniforms,
  uCkGlow: CRACK_GLOW, uCkShape: CRACK_SHAPE, uCkMisc: CRACK_MISC, uCkOn: CRACK_ON,
  uCkAnim: CRACK_ANIM, uCkMask: CRACK_MASK, uCkMaskSel: CRACK_MASKSEL, uCkTime: CLOUD_TIME,
};
/** 잔금 아틀라스 — 브라우저에서 한 번 읽는다. loc-effect-3d/reference/light/crack.md 절차로 만든다(Codex 여러 장 → 이음매 → 2×2).
 *  흑백이라 R8 로 올린다(RedFormat): 2048² + 밉 = 5.6MB(RGBA8 이면 22.4MB). 셰이더는 .r 만 읽는다. */
export const CRACK_MASK_URL = '/textures/crack/crack_atlas.webp';
if (typeof window !== 'undefined') {
  new THREE.TextureLoader().load(CRACK_MASK_URL, (t) => {
    t.format = THREE.RedFormat;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;   // 칸 안에서 fract 로 반복한다
    t.colorSpace = THREE.NoColorSpace;
    t.anisotropy = 4;
    t.name = 'crack.mask.r8';
    CRACK_MASK.value = t;
  }, undefined, () => { /* 장이 없으면 절차 균열만 쓴다 */ });
  // 계측용 — 페이지 안 import() 는 다른 사본이 답할 수 있어 앱이 쓰는 홀더를 창에 건다.
  window.__crack = { CRACK_ON, CRACK_MASK, CRACK_FIELD_TEX, CRACK_FIELD_ON, CRACK_FIELD_XF, CRACK_GLSL_LEN: () => CRACK_GLSL.length };
}

// 필드 조회 + 경계 섞기 — 지형 블록(CRACK_GLSL 이 품는다)과 시공 블록(blockArrayMat — 나무·풀·절벽)이 같이 쓴다.
export const CRACK_FIELD_GLSL = /* glsl */`
uniform sampler2D uCkC;
uniform vec4 uCkXf;
uniform float uCkFOn, uCkEdgeOn;
uniform vec4 uCkBase[${CRACK_SLOTS}], uCkSide[${CRACK_SLOTS}], uCkLeaf[${CRACK_SLOTS}], uCkUnder[${CRACK_SLOTS}], uCkEdgeP[${CRACK_SLOTS}], uCkScorch[${CRACK_SLOTS}];
vec2 ckHash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}
float ckNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  float a = ckHash22(i).x, b = ckHash22(i + vec2(1.0, 0.0)).x, c = ckHash22(i + vec2(0.0, 1.0)).x, d = ckHash22(i + vec2(1.0)).x;
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
// x=가중치(선형 보간) · y=슬롯 id. id 는 흔든 자리의 «가장 가까운 텍셀»에서 읽는다 —
// 보간하면 두 번호 사이(1과 5 사이의 3 = 자수정)가 띠로 나온다. 필드 밖·꺼짐이면 0.
vec2 ckField(vec2 world) {
  if (uCkFOn < 0.5) return vec2(0.0);
  vec2 uv = (world - uCkXf.xy) * uCkXf.zw;
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return vec2(0.0);
  float w = textureLod(uCkC, uv, 0.0).r;
  if (w < 0.003) return vec2(0.0);
  vec2 j = (vec2(ckNoise(world / 11.0), ckNoise(world / 11.0 + 23.1)) - 0.5) * 22.0 + (ckHash22(floor(world * 0.5)) - 0.5) * 2.5;
  vec2 sz = vec2(textureSize(uCkC, 0));
  ivec2 ip = ivec2(clamp(floor((world + j - uCkXf.xy) * uCkXf.zw * sz), vec2(0.0), sz - 1.0));
  return vec2(w, floor(texelFetch(uCkC, ip, 0).g * 255.0 / 32.0 + 0.5));
}
// 경계 섞기 — 층마다 다른 문턱. w 를 «굽힌 경계 거리» t 로 보고(큰 굽이 23m + 잔 노이즈 0.4~1.6m):
//   z 그을림  t 0.05~0.45 — 잔디를 덮지 않고 채도·밝기를 내려 바이옴 그을림 색 쪽으로 곱한다
//   x 재질색  t 0.2~0.6 — 안쪽은 꽉, 바깥 띠는 노이즈 가장자리 얼룩(단계 0·1/3·2/3·1) + 잔디 쪽 듬성한 재 점
//   y 균열    t 0.55~0.9 — 균열·발광·스필은 안쪽 깊이에서만(잔디 위로 안 넘어온다)
//   w 테      재질 경계(t 0.5) 바로 안쪽 그늘. warpK = 굽이 배율.
vec4 ckEdge(vec2 world, float w, float warpK) {
  if (uCkEdgeOn < 0.5) return vec4(w, w, 0.0, 0.0);
  if (w > 0.985) return vec4(1.0, 1.0, 1.0, 0.0);
  float t = w + (ckNoise(world / 23.0) - 0.5) * 0.35 * warpK
              + (ckNoise(world / 1.6 + 17.3) - 0.5) * 0.14
              + (ckNoise(world * 2.5 + 5.7) - 0.5) * 0.06;
  float lv = clamp(floor(ckNoise(world / 1.7 + 71.0) * 3.0 + ckNoise(world * 1.9 + 13.0) * 1.4) / 3.0, 0.0, 1.0);
  float inner = smoothstep(0.45, 0.6, t);
  float fringe = smoothstep(0.2, 0.45, t) * (1.0 - inner) * lv;
  float fleck = smoothstep(0.78 - 0.2 * smoothstep(0.05, 0.4, t), 0.86 - 0.2 * smoothstep(0.05, 0.4, t), ckNoise(world * 2.2 + 41.0)) * smoothstep(0.03, 0.12, t);
  float scorch = smoothstep(0.05, 0.45, t) * (0.75 + 0.25 * ckNoise(world / 2.0 + 9.0));
  return vec4(max(max(inner, fringe), fleck * 0.85), smoothstep(0.55, 0.9, t), scorch, 1.0 - smoothstep(0.0, 0.012, abs(t - 0.5)));
}
`;

// 보로노이 F1·F2 — 세포 중심까지 가장 가까운 거리와 두 번째 거리. F2−F1 이 작으면 세포 경계(=균열)다.
// CRACK_CORE_GLSL 은 CRACK_FIELD_GLSL(해시·노이즈) 뒤에 붙어야 한다 — 시공 블록 재질은 필드를 이미 품고 있어 코어만 더한다.
export const CRACK_CORE_GLSL = /* glsl */`
uniform vec4 uCkGlow[${CRACK_SLOTS}], uCkShape[${CRACK_SLOTS}], uCkMisc[${CRACK_SLOTS}];
uniform vec4 uCkAnim[${CRACK_SLOTS}], uCkMaskSel[${CRACK_SLOTS}];
uniform sampler2D uCkMask;
uniform float uCkOn, uCkTime;
// 아틀라스 한 칸(0~3)을 반복해 읽는다. 밉은 화소 크기로 직접 고른다 — 분기 안이라 미분을 못 쓴다.
float ckMaskAt(vec2 u, float cell, float lod) {
  vec2 off = vec2(mod(cell, 2.0), floor(cell / 2.0)) * 0.5;
  vec2 f = clamp(fract(u), 0.002, 0.998);
  return textureLod(uCkMask, f * 0.5 + off, lod).r;
}
// 균열 해석 노멀 — ckApply·ckCaveApply 가 채우고 재질의 normal 단계가 뷰 공간으로 돌려 더한다(월드, 면 위 성분만).
//   화면 미분이 아니라 식에서 바로 구한 기울기라 도트 경계에서 튀지 않는다. color_fragment 가 normal 단계보다 앞이다.
vec3 ckTilt = vec3(0.0);
// 마지막 ckVoronoi 호출의 «틈 쪽» 단위 방향(p 공간) — 가장 가까운 세포 중심 → 두 번째 중심.
vec2 ckEdgeDir = vec2(0.0);
// x=F1 · y=F2−F1 · z=가장 가까운 세포 난수 · w=그 세포 중심까지의 각진 거리(마름모 — 결정 면)
vec4 ckVoronoi(vec2 p) {
  vec2 ip = floor(p), fp = fract(p);
  float f1 = 8.0, f2 = 8.0, id1 = 0.0; vec2 d1 = vec2(0.0), d2 = vec2(1.0, 0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 g = vec2(float(i), float(j));
    vec2 h = ckHash22(ip + g);
    vec2 r = g + h - fp;
    float d = length(r);
    if (d < f1) { f2 = f1; d2 = d1; f1 = d; id1 = h.x; d1 = r; } else if (d < f2) { f2 = d; d2 = r; }
  }
  ckEdgeDir = normalize(d2 - d1 + 1e-5);
  float a = id1 * 6.2832; vec2 q = mat2(cos(a), sin(a), -sin(a), cos(a)) * d1;
  return vec4(f1, f2 - f1, id1, abs(q.x) * 0.7 + abs(q.y) * 1.3);
}
// 알베도(선형)·이미시브·높이장을 고친다. w=바이옴 가중치 · id=슬롯 · pxM=화소 한 개가 덮는 m(무늬를 접는 데 쓴다)
// · V=표면→카메라 단위 벡터(월드) · hgt=높이장(1 마루 · 0 골 — 범프 노멀·골 그늘이 읽는다).
// 반환 = 무늬가 바닥을 덮은 비율(바이옴 회전을 그만큼 뺀다).
float ckApply(inout vec3 alb, inout vec3 emi, inout float hgt, vec2 world, vec3 V, vec4 E, int id, float pxM, float night) {
  vec4 B = uCkBase[id], G = uCkGlow[id], S = uCkShape[id], M = uCkMisc[id], A = uCkAnim[id], C = uCkScorch[id];
  float w = E.x, wc = E.y;
  float L = dot(alb, vec3(0.2126, 0.7152, 0.0722));
  // 그을림 — 덮지 않고 채도·밝기를 내려 그을림 색 쪽으로 곱한다(색조는 정규화, 밝기 배율은 C.a).
  if (E.z > 0.001) {
    vec3 hue = C.rgb / max(dot(C.rgb, vec3(0.2126, 0.7152, 0.0722)), 0.02);
    alb = mix(vec3(L), alb, 1.0 - 0.65 * E.z) * mix(vec3(1.0), hue * C.a, E.z);
    L = dot(alb, vec3(0.2126, 0.7152, 0.0722));
  }
  alb = mix(alb, B.rgb * (0.6 + 1.2 * L), B.a * w);
  if (wc < 0.001 || S.x < 0.01) return max(B.a * w, E.z * 0.7);
  vec2 p = world / S.x;
  p += (vec2(ckNoise(p * 0.9), ckNoise(p * 0.9 + 31.7)) - 0.5) * M.w * 2.0;
  vec4 v = ckVoronoi(p);
  vec2 ed = ckEdgeDir;
  float aa = 1.0 - smoothstep(0.35, 0.9, pxM / (S.x * max(S.y, 0.04)));
  float m, core, spill, wall = 0.0;
  if (M.x < 0.5) {
    float br = smoothstep(1.0 - S.z, 1.0 - S.z + 0.18, ckNoise(p * 0.55 + 7.3));
    float wid = S.y * (0.55 + 0.7 * ckNoise(p * 1.7));
    // 틈 벽·턱 경사 — 빛 바닥(0)에서 0, 벽(wid)에서 최대, 바깥 wid×3.2 까지 판이 둥글게 내려앉는다.
    wall = smoothstep(0.0, wid * 0.6, v.y) * (1.0 - smoothstep(wid * 0.9, wid * 3.2, v.y)) * br
         * (1.0 - smoothstep(0.3, 1.0, pxM / (S.x * wid * 3.0)));
    m = (1.0 - smoothstep(wid * 0.6, wid * 1.3, v.y)) * br;
    core = (1.0 - smoothstep(0.0, wid * 0.5, v.y)) * br;
    float pR = max(uCkEdgeP[id].x, 0.01);
    float pool = step(1.0 - M.z, fract(v.z * 13.7 + 0.11)) * (1.0 - smoothstep(pR * 0.5, pR, v.x));
    core = max(core, pool); m = max(m, pool);
    spill = max(exp(-v.y / max(wid * 2.2, 0.01)) * br, pool * 0.8);
  } else {
    float on = step(1.0 - S.z, fract(v.z * 7.13 + 0.37));
    m = (1.0 - smoothstep(S.y * 1.2, S.y * 2.0, v.w)) * on;
    core = (1.0 - smoothstep(S.y * 0.3, S.y, v.w)) * on;
    spill = exp(-v.w / max(S.y * 2.4, 0.01)) * on;
  }
  // 시차 깊이 — 빛은 틈 «바닥»(depth m 아래)에 있다. 카메라 쪽으로 밀린 자리에서 다시 읽고,
  // 지금 자리의 틈(m)이 열린 만큼만 보인다 → 비스듬히 보면 가장자리가 빛을 가린다.
  // 낮은 각도에서는 V.y 가 작아 밀림이 몇 배로 커져 빛이 줄무늬로 늘어진다 — 각도가 낮을수록 깊이를 줄인다.
  float dep = uCkMaskSel[id].w * smoothstep(0.12, 0.45, V.y);
  float deep = core;
  if (dep > 0.001) {
    vec2 q = p - V.xz / max(V.y, 0.5) * dep / S.x;
    vec4 v2 = ckVoronoi(q);
    float d2 = M.x < 0.5 ? (1.0 - smoothstep(0.0, S.y * 0.55, v2.y)) : (1.0 - smoothstep(S.y * 0.3, S.y, v2.w)) * step(1.0 - S.z, fract(v2.z * 7.13 + 0.37));
    deep = max(d2 * smoothstep(0.15, 0.6, m), core * 0.35);
  }
  // 잔금 — 이음매 없는 알파 장을 두 번(돌려서) 곱해 반복을 숨긴다. 굵은 균열에서 갈라져 나간 실금이 된다.
  vec4 Q = uCkMaskSel[id];
  vec2 fu = world / (S.x * 1.9);
  float lod = clamp(log2(max(pxM, 1e-4) / (S.x * 1.9) * 1024.0), 0.0, 6.0);
  float ma = ckMaskAt(fu, Q.x, lod);
  float mb = ckMaskAt(mat2(0.8, 0.6, -0.6, 0.8) * fu * 0.73 + 0.37, Q.y, lod);
  float fine = mix(ma, mb, smoothstep(0.35, 0.65, ckNoise(world / max(Q.z, 1.0) + 11.0)));
  fine = smoothstep(0.15, 0.6, fine) * A.w * smoothstep(0.1, 0.55, ckNoise(p * 0.8 + 3.1));
  // 흐름 — 균열을 따라 밝은 띠가 천천히 지나가고(이류 노이즈), 전체가 숨쉬듯 맥동한다.
  float t = uCkTime * A.x;
  float flow = ckNoise(p * 1.6 - vec2(t, t * 0.7)) * 0.7 + ckNoise(p * 4.1 + vec2(t * 1.3, -t)) * 0.3;
  float pulse = 1.0 - A.z + A.z * (0.5 + 0.5 * sin(uCkTime * (1.2 + A.x * 2.0) + v.z * 6.2832));
  float lit = mix(0.55, 1.35, flow) * pulse;
  // 잔금은 «얕은 금» — 어둡게 파이고 빛은 주 균열보다 약하다(주·잔 위계가 깊이로 읽힌다).
  m = max(m, fine * 0.8) * aa * wc; core = max(core, fine * 0.35) * aa * wc; spill *= wc;
  deep = max(deep, fine * 0.28) * aa * wc;
  hgt *= 1.0 - clamp(m * (0.55 + dep * 1.5), 0.0, 0.92);
  alb *= 1.0 - S.w * m;
  // 해석 노멀 — 턱은 틈 쪽으로 기울고(ed), 판마다 조금씩 다르게 기운다(세포 난수). 경사 세기 = 틈 깊이.
  float tk = clamp(uCkMaskSel[id].w * 5.0, 0.0, 1.1);
  vec2 plate = (ckHash22(vec2(v.z * 97.3, 5.1)) - 0.5) * 0.16 * (1.0 - smoothstep(0.2, 0.6, pxM / S.x));
  ckTilt.xz += (ed * wall * tk + plate * step(0.05, tk)) * wc;
  alb *= 1.0 - 0.3 * S.w * wall * wc;
  // 안쪽 벽 반사광 — 틈(m)이되 빛 바닥(deep)이 아닌 벽면이 빛 색으로 물든다.
  alb = mix(alb, G.rgb * 0.22, clamp(m - deep, 0.0, 1.0) * 0.6 * lit);
  alb = mix(alb, G.rgb * 0.6, deep * 0.85);
  alb = mix(alb, alb * 0.6 + G.rgb * 0.4, spill * A.y * 0.5);
  float nk = 1.0 + M.y * night;
  emi += G.rgb * G.a * (deep * lit + spill * A.y * 0.35 * lit) * nk;
  return max(B.a * w, E.z * 0.7);
}
`;
export const CRACK_GLSL = CRACK_FIELD_GLSL + CRACK_CORE_GLSL;

// 동굴 면 — 부피 게이트(선분 캡슐) + 면 법선으로 고른 평면에 ckApply. CRACK_GLSL(또는 FIELD+CORE) 뒤에 붙인다.
//   ckCaveVein 은 place_cave_crystals.vein_teal 과 같은 식·문턱(TEAL_K 1.25)이다(청록 결정 자리 = 청록 균열 자리).
export const CAVE_CRACK_GLSL = /* glsl */`
uniform float uCkCaveOn;
uniform vec4 uCkCaveBox;
uniform vec4 uCkCaveSeg[${CAVE_SEGS}], uCkCaveY[${CAVE_SEGS}];
uniform vec4 uCkCaveTheme;
uniform vec4 uCkCaveMouth;
uniform vec4 uCkCaveMouthY;
float ckCaveVein(vec2 p) { return sin(p.x * 0.031 + p.y * 0.017) + 0.8 * sin(p.y * 0.023 - p.x * 0.011); }
// x=부피 안 정도(0~1) · y=슬롯 id(6 자수정 · 7 청록). 상자 밖이면 루프 없이 0.
vec2 ckCaveZone(vec3 p) {
  if (p.x < uCkCaveBox.x || p.z < uCkCaveBox.y || p.x > uCkCaveBox.z || p.z > uCkCaveBox.w) return vec2(0.0);
  float w = 0.0;
  for (int i = 0; i < ${CAVE_SEGS}; i++) {
    vec4 Y = uCkCaveY[i];
    if (Y.z < 0.5) break;
    vec4 S = uCkCaveSeg[i];
    vec2 ab = S.zw - S.xy;
    float t = clamp(dot(p.xz - S.xy, ab) / max(dot(ab, ab), 1e-3), 0.0, 1.0);
    float d = length(p.xz - S.xy - ab * t);
    float fy = mix(Y.x, Y.y, t);
    float k = (1.0 - smoothstep(Y.z - 8.0, Y.z, d)) * smoothstep(fy - 5.0, fy - 2.0, p.y)
            * (1.0 - smoothstep(fy + Y.w - 4.0, fy + Y.w, p.y));
    w = max(w, k);
  }
  if (uCkCaveTheme.w > 0.5) {
    // 갱구 페이드 — 거리를 노이즈로 흔들어 경계가 불규칙하게 스며든다. halo>0 이면 갱구 밖 둘레(위아래 ±)에도 덩어리로 번진다.
    float d0 = length(p.xz - uCkCaveMouth.xy), d1 = length(p.xz - uCkCaveMouth.zw);
    float dm = min(d0, d1);
    float my = d0 < d1 ? uCkCaveMouthY.x : uCkCaveMouthY.y;
    float nz = 0.6 * ckNoise(p.xz * 0.09 + p.y * 0.05) + 0.4 * ckNoise(p.xz * 0.33 + 7.1);
    w *= smoothstep(2.0, uCkCaveTheme.w, dm + (nz - 0.5) * 26.0);
    if (uCkCaveMouthY.z > 0.001) {
      float R = uCkCaveTheme.w * 1.1;
      float halo = (1.0 - smoothstep(0.2 * R, R, dm + (nz - 0.5) * 34.0))
                 * smoothstep(0.38, 0.62, nz + 0.35 * ckNoise(p.xz * 0.8 + 3.3))
                 * (1.0 - smoothstep(26.0, 44.0, abs(p.y - my - 10.0)));
      w = max(w, halo * uCkCaveMouthY.z);
    }
  }
  return vec2(w, ckCaveVein(p.xz) > 1.25 ? uCkCaveTheme.z : uCkCaveTheme.y);
}
// 결정 속 잔금 한 옥타브 — x=선(0~1) · y=선 «안쪽»(빛 코어) · z=그 칸 난수. 칸마다 켜짐·밝기를 흩고 노이즈로 끊는다.
//   fade = 화면 미분으로 잰 «선 굵기 대비 화소 크기» 페이드 — 멀어지면 가는 선부터 사라져 반짝임(앨리어싱)이 안 난다.
//   keep = 1 이면(주 균열) 사라지는 대신 선을 화소 하나 폭까지만 넓히고 그만큼 어둡게 한다 — 중경에서도 금이 남는다.
vec3 ckCaveOct(vec2 p, float wd, float dens, float pxCell, float keep) {
  vec4 v = ckVoronoi(p);
  float wk = max(wd, pxCell * 0.9 * keep);
  float fade = mix(1.0 - smoothstep(0.35, 1.1, pxCell / max(wd, 1e-3)), wd / max(wk, 1e-4), keep);
  wd = wk;
  float on = step(1.0 - dens, fract(v.z * 13.71 + 0.17)) * smoothstep(0.28, 0.5, ckNoise(p * 0.61 + v.z * 3.0));
  float wv = wd * (0.45 + 1.1 * ckNoise(p * 1.9 + 4.0));
  float line = (1.0 - smoothstep(wv * 0.5, wv * 1.25 + pxCell * 0.6, v.y)) * on * fade;
  float core = (1.0 - smoothstep(0.0, wv * 0.5 + pxCell * 0.3, v.y)) * on * fade;
  return vec3(line, core, v.z);
}
// 파편 결정면 — x,y = 세포 중심→점 벡터(조각 안 기울기 명암) · z = 조각 난수 · w = F2−F1(조각 경계).
vec4 ckFacet(vec2 p) {
  vec2 ip = floor(p), fp = fract(p);
  float f1 = 8.0, f2 = 8.0, id1 = 0.0; vec2 d1 = vec2(0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 g = vec2(float(i), float(j));
    vec2 h = ckHash22(ip + g);
    vec2 r = g + h - fp;
    float d = dot(r, r);
    if (d < f1) { f2 = f1; f1 = d; id1 = h.y; d1 = r; } else if (d < f2) { f2 = d; }
  }
  return vec4(d1, id1, sqrt(f2) - sqrt(f1));
}
// n = 면 법선(월드, 축 정렬 블록) · V = 표면→카메라 단위 벡터 · z = ckCaveZone 결과 · geo = 1 이면(지형) 정동 3겹 재질을
//   칸 좌표 노이즈로 골라 입힌다(reskin_ns3_geode.py 의 층 문턱과 같은 0.56·0.65 · 바닥 +0.16). 블록 0 = 안감(빛 균열) · 2 = 현무암·방해석(마른 금).
//   지형 띠는 «블록 칸» 단위로 고른다(경계가 계단진 블록 모양 — 연속 좌표·큰 굽이를 쓰면 블록 세상에 매끈한 곡선이 생긴다).
//   경계 근처 칸 안에서만 3.5텍셀 덩어리 흩뿌림 · 높이장 · 경계 그늘 · 가장자리 자갈(skill pro-world-road 의 «칸 안» 요리).
//   현무암·방해석에는 «젖은 돌 반짝임»(픽셀 1.2%, 시선 반사 방향에 따라 켜짐)을 따로 둔다.
//   섞기 층: 얼룩 명암(7m·2.3m) · 픽셀 요철(화소보다 잘면 끈다) · 균열 몰림 덮개(13m) · 빛 웅덩이(19m) · 마른 금(9m) · 띠 경계 픽셀 흔들기. 반환 = 덮은 비율.
//   지표 바이옴의 ckApply 와 «다른 식»이다 — 보로노이 주 균열 1옥타브(강한 이미시브·캐비티·범프) + 잔금 아틀라스 두 축척
//   (중간 금 칸×1.35 · 실금 칸×0.8, 약한 반짝임). 좌표를 노이즈로 두 번 비틀어(도메인 워프) 격자 망을 깬다.
//   보로노이는 한 번만 부른다 — 이 함수는 지형·시공 블록 프로그램에 늘 컴파일되므로 원문 크기가 곧 첫 로딩 비용이다.
float ckCaveApply(inout vec3 alb, inout vec3 emi, inout float hgt, vec3 p, vec3 n, vec3 V, float pxM, vec2 z, float geo) {
  vec3 an = abs(n);
  vec2 q; vec3 Vl;
  if (an.y >= an.x && an.y >= an.z) { q = p.xz; Vl = vec3(V.x, V.y * sign(n.y), V.z); }
  else if (an.x >= an.z) { q = p.zy + vec2(0.0, 517.0); Vl = vec3(V.z, V.x * sign(n.x), V.y); }
  else { q = p.xy + vec2(331.0, 0.0); Vl = vec3(V.x, V.z * sign(n.z), V.y); }
  int id = int(z.y + 0.5);
  float w = z.x;
  vec4 B = uCkBase[id], G = uCkGlow[id], S = uCkShape[id], A = uCkAnim[id], Q = uCkMaskSel[id];
  float am = geo > 1.5 ? 0.0 : 1.0;
  float pf = 1.0 - smoothstep(0.025, 0.07, pxM);
  float px = mix(0.5, ckHash22(floor(q * 16.0) + 3.7).x, pf);
  if (geo > 0.5 && geo < 1.5) {
    vec3 cb = floor(p - n * 0.02);
    float g = 0.65 * ckNoise(cb.xz / 9.0 + cb.y * 0.137) + 0.35 * ckNoise(cb.zy / 4.0 + cb.x * 0.071);
    float cl = mix(0.5, ckHash22(floor(q * 16.0 / 3.5) + 7.1).x, pf);
    float cl2 = mix(0.5, ckHash22(floor(q * 16.0 / 1.5) + 2.3).x, pf);
    float lv = mix(0.5, ckHash22(floor(q * 16.0 / 2.0) + 61.7).x, pf);
    g = clamp((g - 0.18) / 0.64, 0.0, 1.0) + (n.y > 0.5 ? 0.16 : 0.0) + (cl - 0.5) * 0.16 + (cl2 - 0.5) * 0.07;
    // 경계 섞기(skill pro-world-road 의 칸 안 요리) — 덩어리 흩뿌림 두 겹 · 단계 투명도 4단 · 높이장(밝은 안감 픽셀이 이긴다)
    float ht = (px - 0.5) * 0.06;
    float s1 = clamp(floor(smoothstep(0.52, 0.6, g - ht) * 3.0 + lv) / 3.0, 0.0, 1.0);
    float s2 = clamp(floor(smoothstep(0.61, 0.69, g + ht) * 3.0 + lv) / 3.0, 0.0, 1.0);
    float th = clamp(uCkCaveTheme.x, 0.0, 1.0);
    float ts = clamp(uCkCaveTheme.x - 1.0, 0.0, 1.0);   // 흑요 별빛 — 검은 흑요석 껍질 · 회청 띠 · 청남 안감
    vec3 cAm = mix(mix(vec3(0.03, 0.012, 0.075), vec3(0.22, 0.1, 0.46), px * px),
                   mix(vec3(0.06, 0.012, 0.004), vec3(0.42, 0.09, 0.02), px * px), th);
    vec3 cCa = mix(mix(vec3(0.2, 0.17, 0.24), vec3(0.6, 0.54, 0.68), px),
                   mix(vec3(0.05, 0.05, 0.07), vec3(0.17, 0.16, 0.2), px), th);
    vec3 cBa = mix(mix(vec3(0.02, 0.018, 0.03), vec3(0.09, 0.075, 0.115), px),
                   mix(vec3(0.012, 0.014, 0.022), vec3(0.05, 0.055, 0.08), px), th);
    cAm = mix(cAm, mix(vec3(0.004, 0.006, 0.016), vec3(0.06, 0.09, 0.2), px * px), ts);
    cCa = mix(cCa, mix(vec3(0.018, 0.02, 0.028), vec3(0.1, 0.105, 0.13), px), ts);
    cBa = mix(cBa, mix(vec3(0.002, 0.002, 0.004), vec3(0.02, 0.02, 0.03), px), ts);
    vec3 gc = mix(mix(cAm, cCa, s1), cBa, s2);
    am = 1.0 - s1;
    float ge = min(abs(g - 0.56), abs(g - 0.65));
    gc *= mix(0.66, 1.0, smoothstep(0.0, 0.06, ge));
    gc = mix(gc, mix(vec3(0.5, 0.44, 0.6), vec3(0.55, 0.2, 0.06), th), step(ge, 0.03) * step(0.85, ckHash22(floor(q * 16.0) + 23.9).x) * pf);
    hgt *= mix(1.0, 0.75 + 0.5 * px, w * (1.0 - smoothstep(0.0, 0.08, ge)) * pf);
    alb = mix(alb, gc, w);
  } else if (geo < 0.5) {
    float L = dot(alb, vec3(0.2126, 0.7152, 0.0722));
    alb = mix(alb, B.rgb * (0.6 + 1.2 * L), B.a * w);
  }
  float mac = ckNoise(q / 7.0 + 3.1) * 0.6 + ckNoise(q / 2.3 + 8.7) * 0.4;
  alb *= mix(1.0, 0.66 + 0.62 * mac, w);
  // 파편 결정면 — 조각 크기를 몰림 노이즈로 세 단(1.15·0.62·0.36m)에 가르고, 좌표를 비틀어 조각 모양·간격을 흩는다.
  //   조각마다 기울기 명암·능선·색조를 흩는다. 좌표는 1/16m 픽셀 눈금으로 양자화 — 조각 경계가 픽셀 계단이다.
  vec2 qp = (floor(q * 16.0) + 0.5) / 16.0;
  float sv = ckNoise(qp / 5.0 + 2.9) + (ckHash22(floor(qp / 1.6) + 5.1).x - 0.5) * 0.35;
  float fsc = sv < 0.4 ? 1.15 : sv < 0.6 ? 0.62 : 0.36;
  vec2 fq = qp / fsc + (vec2(ckNoise(qp * 0.9 + 4.1), ckNoise(qp * 0.9 + 12.7)) - 0.5) * 0.55;
  vec4 fc = ckFacet(fq + 40.0);
  vec2 fdir = ckHash22(vec2(fc.z * 91.7, 3.3)) * 2.0 - 1.0;
  // 조각 명암은 좁게 — 기울기는 해석 노멀(ckTilt)이 빛으로 낸다. 알베도로 크게 흔들면 조각이 붙인 스티커로 읽힌다.
  float ftone = 0.84 + 0.24 * fract(fc.z * 7.31) + dot(fc.xy, fdir) * 0.18;
  float fseam = 1.0 - smoothstep(0.0, 0.05 + 0.6 * pxM / fsc, fc.w);
  float ridge = (1.0 - smoothstep(0.0, 0.12, fc.w)) * step(0.55, fract(fc.z * 3.7)) * (1.0 - fseam);
  float fvis = 1.0 - smoothstep(0.08, 0.3, pxM / fsc);
  float fh = fract(fc.z * 11.3);
  vec3 fcol = fh < 0.3 ? vec3(0.17, 0.07, 0.42) : fh < 0.52 ? vec3(0.4, 0.1, 0.38) : fh < 0.72 ? vec3(0.09, 0.11, 0.48)
    : fh < 0.92 ? vec3(0.5, 0.4, 0.82) : vec3(0.08, 0.4, 0.5);
  vec3 fmag = fh < 0.45 ? vec3(0.42, 0.08, 0.02) : fh < 0.7 ? vec3(0.6, 0.2, 0.03) : fh < 0.9 ? vec3(0.12, 0.1, 0.14) : vec3(0.08, 0.2, 0.55);
  vec3 fstar = fh < 0.5 ? vec3(0.02, 0.025, 0.045) : fh < 0.8 ? vec3(0.07, 0.11, 0.3) : fh < 0.94 ? vec3(0.16, 0.13, 0.07) : vec3(0.14, 0.18, 0.28);
  fcol = mix(mix(fcol, fmag, clamp(uCkCaveTheme.x, 0.0, 1.0)), fstar, clamp(uCkCaveTheme.x - 1.0, 0.0, 1.0));
  float fwa = w * am * fvis;
  // 색조만 바꾸고 밝기는 바탕을 따른다 — 어두운 흑요석 위에 밝은 조각이 뜨지 않는다.
  float fL = dot(alb, vec3(0.2126, 0.7152, 0.0722));
  vec3 fhue = fcol / max(dot(fcol, vec3(0.2126, 0.7152, 0.0722)), 0.01);
  alb = mix(alb, mix(alb, min(fhue * fL, vec3(1.0)), 0.45) * ftone, fwa);
  alb *= 1.0 - 0.5 * fseam * fwa;
  alb = mix(alb, alb * (0.8 + 0.4 * clamp(ftone - 0.5, 0.0, 1.0)), w * (1.0 - am) * fvis * 0.7);
  alb *= 1.0 - 0.3 * fseam * w * (1.0 - am) * fvis;
  float facing = 1.0 - max(dot(n, V), 0.0);
  emi += fcol * (ftone * 0.035 + ridge * 0.16) * (0.5 + facing) * fwa;
  // 깨진 원석 칩 — 조각마다 다른 축척(0.2~0.45배)의 잔 보로노이 한 칸을 통째로 칩으로 쓴다(다각형·크기 무작위).
  //   몰림 노이즈(3.3m)가 켜짐 문턱을 흔들어 간격이 고르지 않다. 안감은 더 잦고 현무암은 드물다.
  float csc = fsc * mix(0.2, 0.45, fract(fc.z * 17.3));
  vec4 cc = ckFacet(fq * fsc / csc + 71.0);
  float cdens = mix(0.95, 0.84, am) - 0.07 * smoothstep(0.4, 0.8, ckNoise(qp / 3.3 + 9.1));
  float chipOn = step(cdens, fract(cc.z * 41.7)) * w * fvis * (1.0 - smoothstep(0.1, 0.3, pxM / csc));
  float chip = smoothstep(0.07, 0.11 + pxM / csc, cc.w) * chipOn;
  float crim = (1.0 - smoothstep(0.0, 0.07 + pxM / csc, cc.w)) * chipOn;
  vec3 gem = mix(fcol * 2.2, mix(mix(vec3(0.78, 0.66, 1.0), vec3(1.0, 0.62, 0.25), clamp(uCkCaveTheme.x, 0.0, 1.0)), vec3(0.72, 0.86, 1.0), clamp(uCkCaveTheme.x - 1.0, 0.0, 1.0)), 0.35 + 0.3 * clamp(cc.x * 2.5, -1.0, 1.0));
  alb = mix(alb * (1.0 - 0.55 * crim), gem, chip);
  emi += gem * chip * (0.12 + 0.18 * facing) * (0.7 + 0.3 * fract(fc.z * 13.1));
  hgt = mix(hgt, 1.0, chip * 0.7);
  hgt *= mix(1.0, 0.85 + 0.3 * clamp(ftone - 0.4, 0.0, 1.0), w * fvis) * (1.0 - 0.35 * fseam * w * fvis);
  hgt *= mix(1.0, 0.9 + 0.2 * ckHash22(floor(q * 16.0) + 19.1).x, w * (1.0 - smoothstep(0.008, 0.02, pxM)));
  float patchK = smoothstep(0.32, 0.68, ckNoise(q / 13.0 + p.y * 0.031 + 1.7));
  float pool = 0.35 + 1.1 * smoothstep(0.2, 0.8, ckNoise(q / 19.0 + 5.5));
  float wd = w * (1.0 - am) * smoothstep(0.45, 0.8, ckNoise(q / 9.0 + 11.3));
  float wa = w * am;
  w = wa * patchK;
  vec2 wq = q + (vec2(ckNoise(q * 0.37), ckNoise(q * 0.37 + 17.1)) - 0.5) * 1.1;
  wq += (vec2(ckNoise(wq * 1.7 + 5.3), ckNoise(wq * 1.7 + 9.1)) - 0.5) * 0.25;
  float c1 = S.x;
  vec3 o1 = ckCaveOct(wq / c1, S.y, S.z, pxM / c1, 1.0);
  // 해석 노멀(면 좌표 q) — 조각마다 기운 결정면 + 주 균열 벽이 틈 쪽으로 기운다. 아래에서 월드 축으로 옮긴다.
  vec2 tq = fdir * 0.2 * fwa + ckEdgeDir * clamp(o1.x - o1.y, 0.0, 1.0) * 0.7 * (w + wd);
  if (an.y >= an.x && an.y >= an.z) ckTilt += vec3(tq.x, 0.0, tq.y);
  else if (an.x >= an.z) ckTilt += vec3(0.0, tq.y, tq.x);
  else ckTilt += vec3(tq.x, tq.y, 0.0);
  alb *= 1.0 - 0.6 * o1.x * wd;
  hgt *= 1.0 - 0.5 * o1.x * wd;
  float c2 = c1 * 1.35, cf = c1 * 0.8;
  float l2 = clamp(log2(max(pxM, 1e-4) / c2 * 1024.0), 0.0, 6.0), lf = clamp(log2(max(pxM, 1e-4) / cf * 1024.0), 0.0, 6.0);
  float ma = ckMaskAt(mat2(0.8, 0.6, -0.6, 0.8) * wq / c2 + 13.7, Q.x, l2);
  float mb = ckMaskAt(mat2(0.6, 0.8, -0.8, 0.6) * wq / cf + 0.37, Q.y, lf);
  float mid = smoothstep(0.3, 0.75, ma) * smoothstep(0.25, 0.55, ckNoise(wq * 0.8 + 7.7)) * (1.0 - smoothstep(0.06, 0.25, pxM / c2 * 6.0));
  float fine = smoothstep(0.25, 0.7, mb) * A.w * smoothstep(0.3, 0.6, ckNoise(wq * 1.3 + 3.1)) * (1.0 - smoothstep(0.06, 0.25, pxM / cf * 6.0));
  float br1 = 0.55 + 0.45 * fract(o1.z * 7.31);
  float dep = Q.w * smoothstep(0.12, 0.45, Vl.y);
  float deep = o1.y;
  float t = uCkTime * A.x;
  float flow = ckNoise(wq / c1 * 1.3 - vec2(t, t * 0.7)) * 0.7 + ckNoise(wq / c1 * 3.7 + vec2(t * 1.3, -t)) * 0.3;
  float pulse = 1.0 - A.z + A.z * (0.5 + 0.5 * sin(uCkTime * (1.2 + A.x * 2.0) + o1.z * 6.2832));
  float lit = mix(0.55, 1.35, flow) * pulse * pool;
  float m1 = o1.x * w, m2 = mid * w, mf = fine * wa;
  deep *= w;
  hgt *= 1.0 - clamp(m1 * (0.5 + dep * 2.0) + m2 * 0.15, 0.0, 0.9);
  alb *= 1.0 - S.w * m1;
  alb = mix(alb, G.rgb * 0.45, clamp(m1 - deep, 0.0, 1.0) * 0.5 * lit);
  alb = mix(alb, G.rgb * 0.6, deep * 0.85 * br1);
  alb = mix(alb, mix(alb, G.rgb, 0.35) * 1.15, clamp(m2 * 0.5 + mf * 0.3, 0.0, 1.0));
  emi += G.rgb * G.a * (deep * br1 * lit + m2 * 0.22 * (0.6 + 0.4 * lit) + mf * 0.09 * lit);
  emi += G.rgb * G.a * A.y * 0.12 * smoothstep(0.0, 1.0, m1) * lit;
  // 반짝임 — 고른 점묘가 아니라 몰림 얼룩(4m·1.3m) 안에서만, 문턱·밝기·박자를 픽셀마다 흩는다.
  float hs = ckHash22(floor(q * 16.0) + 41.3).x;
  float hb = ckHash22(floor(q * 16.0) + 77.9).x;
  float clump = smoothstep(0.58, 0.82, 0.65 * ckNoise(q / 4.1 + 13.7) + 0.35 * ckNoise(q / 1.3 + 2.1));
  float thr = mix(0.9995, 0.986, clump);
  float tw = pow(0.5 + 0.5 * sin(uCkTime * (0.25 + 1.4 * fract(hs * 37.0)) + hs * 80.0), mix(4.0, 14.0, hb));
  float spk = step(thr, hs) * tw * mix(0.45, 1.25, hb);
  emi += mix(G.rgb, vec3(1.0), 0.25 + 0.4 * hb) * spk * wa * (1.0 - smoothstep(0.05, 0.14, pxM));
  float wv = pow(max(dot(reflect(-V, n), vec3(0.0, 1.0, 0.0)) * 0.5 + 0.5, 0.0), 2.0);
  emi += vec3(0.85, 0.8, 1.0) * step(mix(0.9997, 0.993, clump), hs) * tw * wv * 0.45 * z.x * (1.0 - am) * (1.0 - smoothstep(0.03, 0.09, pxM));
  w = z.x;
  return B.a * w;
}
`;
